// ─── Mock Data — replace with real API response ────────────────────────────

export interface ScoreCards {
  overall: number;
  experiences: number;
  skills: number;
  language: number;
  education: number;
  certificates: number;
}

export interface RadarItem {
  category: string;
  value: number;
}

export interface Strength {
  title: string;
  description: string;
}

export interface Weakness {
  title: string;
  description: string;
}

export interface Improvement {
  section: string;
  suggestion: string;
  priority: "high" | "medium" | "low";
}

export interface AnalysisResult {
  candidateName: string;
  candidateEmail: string;
  candidateTitle: string;
  scorecards: ScoreCards;
  aiSummary: string;
  radarData: RadarItem[];
  strengths: Strength[];
  weaknesses: Weakness[];
  improvements: Improvement[];
  atsScore: number;
  atsIssues: string[];
}

export const MOCK_ANALYSIS_RESULT: AnalysisResult = {
  candidateName: "Nguyen Van A",
  candidateEmail: "nguyenvana@email.com",
  candidateTitle: "Senior Frontend Developer",
  scorecards: {
    overall: 78,
    experiences: 85,
    skills: 82,
    language: 70,
    education: 75,
    certificates: 60,
  },
  aiSummary:
    "This candidate demonstrates strong frontend engineering capabilities with 5+ years of hands-on experience in React and TypeScript ecosystems. Their portfolio shows a consistent track record of delivering scalable, performant web applications. The resume is well-structured and ATS-friendly, though some sections could be strengthened with more quantifiable achievements. Overall, this is a competitive profile for mid-to-senior frontend roles.",
  radarData: [
    { category: "Experience", value: 85 },
    { category: "Skills", value: 82 },
    { category: "Language", value: 70 },
    { category: "Certificates", value: 60 },
    { category: "Education", value: 75 },
  ],
  strengths: [
    {
      title: "Strong Technical Stack",
      description:
        "Proficient in React, TypeScript, Next.js and modern frontend tooling. Demonstrates depth in component architecture and state management.",
    },
    {
      title: "Relevant Work Experience",
      description:
        "5+ years of experience across product companies with increasing responsibility. Clear career progression from junior to senior level.",
    },
    {
      title: "Project Impact",
      description:
        "Multiple projects show measurable impact: reduced bundle size by 40%, improved lighthouse scores from 65 to 94.",
    },
  ],
  weaknesses: [
    {
      title: "Limited Leadership Experience",
      description:
        "No clear evidence of mentoring junior developers or leading cross-functional teams, which may be a gap for senior-level positions.",
    },
    {
      title: "Sparse Certifications",
      description:
        "Only one listed certification. Adding AWS, GCP, or relevant industry certifications would strengthen the profile significantly.",
    },
    {
      title: "Weak Soft Skills Section",
      description:
        "The resume lacks specific examples of communication, collaboration, or problem-solving in team settings.",
    },
  ],
  improvements: [
    {
      section: "Work Experience",
      suggestion:
        "Quantify your achievements with metrics. Instead of 'improved performance', write 'improved page load time by 35%, resulting in 20% higher conversion rate'.",
      priority: "high",
    },
    {
      section: "Summary / Objective",
      suggestion:
        "Add a compelling 3-line summary at the top tailored to your target role. Mention your specialization, years of experience, and key value proposition.",
      priority: "high",
    },
    {
      section: "Skills Section",
      suggestion:
        "Organize skills into categories (Frontend, Backend, Tools, Soft Skills) for better readability and ATS parsing.",
      priority: "medium",
    },
    {
      section: "Certifications",
      suggestion:
        "Pursue at least one industry certification relevant to your target stack (e.g., AWS Cloud Practitioner, Meta Frontend Certificate).",
      priority: "medium",
    },
    {
      section: "Keywords",
      suggestion:
        "Include role-specific keywords like 'micro-frontend', 'CI/CD', 'agile', 'code review' to improve ATS match rates.",
      priority: "low",
    },
  ],
  atsScore: 72,
  atsIssues: [
    "Missing standard section headers (use 'Work Experience' instead of 'Career Journey')",
    "Some bullet points exceed 2 lines — keep them concise for ATS parsing",
    "Phone number format may not be parsed correctly — use international format",
    "No LinkedIn URL detected in contact section",
  ],
};
